import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/** The address carries the file's own field name, matching the standalone page's `media-file/:id`. */
export const MEDIA_FILE_QUERY_PARAM = "id";

type MediaFileModalAddressOptions = {
  readonly isOpen: boolean;
  readonly fileId: string | null;
  readonly open: (fileId: string) => void;
  readonly close: () => void;
};

/**
 * Keeps the open file and the address in step, in both directions: opening writes the id, Back clears it,
 * and an id already in the address opens the modal on it.
 *
 * The user flow asks for the id to be "kept in the navigation history", so opening pushes a real entry and
 * closing pops it, which is also what returns the page behind to the state it was in. That pop is a genuine
 * navigation and the router sees it, so a close costs one render of the pages that read the location. A close
 * with no entry to pop, because the address named the file before the modal did, clears the id through the
 * router for the same reason: the router's location holds the id too, and whatever rebuilds an address from
 * it next, such as the language switcher, would otherwise put the id back and reopen the file.
 *
 * Nothing else reaches the router. The app reads the location at its root, so a router write re-renders every page under
 * it, and on a chat that is the whole conversation and each of its messages. Opening and stepping therefore
 * write the address through the History API instead: the id still lands in the address, so copying, reloading
 * and sharing all behave the same, but no page re-renders for a file it is not showing.
 */
export default function useMediaFileModalAddress({ isOpen, fileId, open, close }: MediaFileModalAddressOptions): void {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  // The opening entry marks itself, so closing knows to pop it. It lives in the history entry rather than a
  // ref because a remount while the modal is open would wipe a ref and leave the entry behind on close.
  const refWasOpen = useRef(false);
  // The file the modal last showed. A closed modal whose address still names it was just closed, so the
  // id comes off the address; any other file in the address reopens instead (a reload, a shared link,
  // an in-app link), with the address already set so the first step from it replaces.
  const refShownFileId = useRef<string | null>(null);
  // Back and Forward move the address without re-rendering anything, because the writes below never reach the
  // router. This is what tells the modal the id has gone, so it closes with the address instead of staying up.
  const [stateAddressChangeCount, setStateAddressChangeCount] = useState(0);
  useEffect(() => {
    const onPopState = () => setStateAddressChangeCount((count) => count + 1);
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    // Read from the live history entry, not the router's copy: the writes below go through the History API,
    // so the router never sees the marker land.
    const didPushEntry = (window.history.state as { mediaFileModal?: boolean } | null)?.mediaFileModal === true;
    // The id is read from whichever source still names a file. The writes below go through the History API,
    // so the router's `search` lags behind the address; but under a memory router the address never moves at
    // all. Taking the router's value first and falling back to the live address covers both, and a Back that
    // drops the id clears it from both, so the modal follows it down rather than staying up over the page.
    const routerParams = new URLSearchParams(search);
    const params = routerParams.get(MEDIA_FILE_QUERY_PARAM) ? routerParams : new URLSearchParams(window.location.search);
    const rawParam = params.get(MEDIA_FILE_QUERY_PARAM);
    const currentParam = rawParam !== null && rawParam.length > 0 ? rawParam : null;

    if (isOpen && fileId !== null) {
      // Set before the early return: a file deep-linked into the address is already "open", so the
      // first step from it must replace rather than push a second entry.
      const isFirstEntry = !refWasOpen.current;
      // The address had the id and lost it: Back was pressed, and the modal follows the address.
      if (currentParam === null && !isFirstEntry) {
        close();
        return;
      }
      refWasOpen.current = true;
      refShownFileId.current = fileId;

      if (currentParam === fileId) {
        return;
      }
      params.set(MEDIA_FILE_QUERY_PARAM, fileId);
      const nextSearch = params.toString();

      // A step: the modal is already the entry the address describes, so the id is swapped in place without
      // telling the router. Nothing outside the modal is watching this id, and a router write here would
      // re-render every page that reads the location.
      if (!isFirstEntry) {
        window.history.replaceState(window.history.state, "", `${window.location.pathname}?${nextSearch}`);
        return;
      }

      // Pushed through the History API rather than the router: the app reads the location at its root, so a
      // router write here re-renders every page under it, on a chat every message it holds.
      window.history.pushState({ ...(window.history.state as object | null), mediaFileModal: true }, "", `${window.location.pathname}?${nextSearch}`);
      return;
    }

    refWasOpen.current = false;
    if (currentParam === null) {
      refShownFileId.current = null;
      return;
    }
    if (currentParam === refShownFileId.current) {
      refShownFileId.current = null;
      if (didPushEntry) {
        // A real navigation, which the router does observe: this is the one place the modal cannot avoid a
        // re-render, because popping the entry is what makes Back close the modal rather than leave the page.
        window.history.back();
        return;
      }
      // No entry of our own to pop, so the id comes off the address in place, through the router: it named the
      // file first, so its location must lose the id too, or the next address built from it brings the id back.
      params.delete(MEDIA_FILE_QUERY_PARAM);
      const cleared = params.toString();
      navigate({ pathname, search: cleared.length > 0 ? `?${cleared}` : "" }, { replace: true });
      return;
    }
    refWasOpen.current = true;
    open(currentParam);
  }, [isOpen, fileId, pathname, search, stateAddressChangeCount, open, close, navigate]);
}
